import { HeliumRawClient } from '@msi-alta/helium-client';
import { useMemo } from 'react';
import set from 'lodash/set';

import { CsvExportButton } from 'new-components/DLS/OpTableCore/components';
import { UseOpQueryOptions } from 'utils/customHooks/useOpQuery';
import {
  OpTableColumn,
  OpTableRecordType,
  TableState,
} from 'new-components/DLS/OpTableCore/types';
import { ensurePayloadAndQuery } from 'utils/ensurePayloadAndQuery';
import { useFetchAllViaCursor } from 'utils/customHooks/useFetchAllViaCursor';

import { createQueryStringParamsFromTableState } from '../createQueryStringParamsFromTableState';
import { GetterApiEndpointNames, QueryStringParams } from '../types';
import { dynamicallyPopulateQueryOptions } from '../dynamicallyPopulateQueryOptions';

type HeliumClientResponse<T extends GetterApiEndpointNames> = Awaited<
  ReturnType<HeliumRawClient[T]>
>;
type HeliumClientResponseData<T extends GetterApiEndpointNames> =
  HeliumClientResponse<T>['json']['data'];

interface CursorDataFetchCsvExportButtonProps<
  T extends GetterApiEndpointNames,
  Selection,
  RecordType extends OpTableRecordType,
> {
  opQueryProps: UseOpQueryOptions<T, Selection>;
  debouncedTableState: TableState<RecordType>;
  columnsFilterCallback?: (column: OpTableColumn<RecordType>) => boolean;
  filename?: string;
}

// Specific to cursor based data fetch tables
export const CursorDataFetchCsvExportButton = <
  T extends GetterApiEndpointNames,
  Selection,
  RecordType extends OpTableRecordType,
>({
  opQueryProps,
  debouncedTableState,
  columnsFilterCallback,
  filename,
}: CursorDataFetchCsvExportButtonProps<T, Selection, RecordType>) => {
  // useMemo ensures that the array reference remains stable unless its dependencies actually change, preventing unnecessary re-renders and cache misses in TanStack Query
  const { parametersArray, withQuery } = useMemo(
    () =>
      ensurePayloadAndQuery(
        opQueryProps.apiEndpointName,
        opQueryProps.parameters,
      ),
    [opQueryProps.apiEndpointName, opQueryProps.parameters],
  );

  // Find the index of the queries (last object)
  const queriesIndex = parametersArray.findIndex(
    (param?: number | string | {} | null) => typeof param === 'object',
  );

  if (withQuery && queriesIndex === -1) {
    // ensurePayloadAndQuery guarantees the presence of a queries object, so throw since something is way off...
    throw new Error(
      `Critical: ensurePayloadAndQuery did not ensure a query object for API endpoint ${opQueryProps.apiEndpointName}`,
    );
  }

  if (withQuery) {
    // Add queryStringParams to their correct spot in parameters array
    const derivedParams = createQueryStringParamsFromTableState({
      tableState: debouncedTableState,
      isUsingCursor: true,
    });

    // Create a new object without q and filter if they're not in derivedParams
    const baseParams = {
      ...(parametersArray[queriesIndex] as QueryStringParams),
    };

    // Make sure we don't have limit or offset in the baseParams as we're using cursor pagination
    delete baseParams.limit;
    delete baseParams.offset;

    // Make sure if q or filter are not in derivedParams, we remove them from the baseParams as it means they were set to be empty/not present
    if (!('q' in derivedParams)) delete baseParams.q;
    if (!('filter' in derivedParams)) delete baseParams.filter;

    // TODO: baseParams.options is the existing options, not just the base options, so hiding a column doesn't change the return value
    // BUT it only is an issue when hiding a column; when showing a column it updates
    // so really we're only ever returning more data than needed, not less, so it's not a huge issue
    const finalQueryOptions = dynamicallyPopulateQueryOptions({
      tableState: debouncedTableState,
      queryOptions: baseParams.options,
    });
    // If this is empty we can't assign it to options, since we don't need options
    if (finalQueryOptions) {
      baseParams.options = finalQueryOptions;
    }

    parametersArray[queriesIndex] = {
      ...baseParams,
      ...derivedParams,
    };
  }

  const { isFetching, hasNextPage, refetch, totalData } = useFetchAllViaCursor({
    apiEndpointName: opQueryProps.apiEndpointName,
    parameters: parametersArray,
    enabled: false,
    queryKey: [
      opQueryProps.apiEndpointName,
      JSON.stringify(parametersArray),
      JSON.stringify(debouncedTableState),
    ],
  });

  // Can filter columns with createColumns
  const computedColumns = debouncedTableState.columns.filter(
    (column) => !column.hidden && (columnsFilterCallback?.(column) ?? true),
  ) as OpTableColumn<HeliumClientResponseData<T>>[];

  const dataForCsv =
    isFetching || hasNextPage
      ? []
      : totalData.current.map((record) => {
          let newRecord = record;

          computedColumns.forEach(({ key, createCsvExportCellValue }) => {
            if (createCsvExportCellValue) {
              // To be efficient, as we could potentially have a lot of records, we want to avoid creating a new object for each record unless we must
              if (newRecord === record) newRecord = structuredClone(record);

              set(
                newRecord,
                String(key), // The key can be a path (e.g. 'userCustomFields.0.value')
                createCsvExportCellValue(record as HeliumClientResponseData<T>),
              );
            }
          });

          return newRecord;
        });

  // Manually fetches each query on export button click
  const onExportClick = () => {
    refetch();
  };
  return (
    <CsvExportButton
      /**
       * As there is a small delay between the last page being
       * fetched and the refetch we want to make sure we consider
       * the data loading if there are more pages to fetch
       */
      isLoading={isFetching || hasNextPage}
      onClick={onExportClick}
      data={dataForCsv}
      columns={computedColumns}
      filename={filename}
    />
  );
};
